import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useRef } from 'react';
import { z } from 'zod';

import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Confetti, type ConfettiRef } from '@/components/ui/confetti';
import type { LowerWorkerType } from '@/constants/workerType';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';

const CompletedSchema = z.object({
  addedPersonGivenName: z.string(),
  addedPersonFamilyName: z.string().optional(),
  addedPersonID: z.string(),
});

export const Route = createFileRoute(
  '/_staff/person/add/$workerType/completed',
)({
  validateSearch: zodValidator(CompletedSchema),
  loaderDeps: ({ search }) => ({ search }),
  component: () => <Completed />,
});

function Completed() {
  const confettiRef = useRef<ConfettiRef>(null);
  const { session } = useAuthStore();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.add.form.navigation.step.completed',
  });
  const { workerType } = Route.useParams();
  const { search } = Route.useLoaderDeps();
  const navigate = useNavigate();

  return (
    <VStack className="flex justify-center items-center">
      <Confetti ref={confettiRef} />
      <AttributeCard
        title="Result"
        actionButton={
          <ActionButton
            variant="secondary"
            onClick={() =>
              navigate({
                to: '/person/$id',
                params: { id: search.addedPersonID },
              })
            }
          >
            {t('goToProfile')}
          </ActionButton>
        }
        className="p-4 w-2/4"
      >
        <VStack>
          <HStack className="items-center">
            <Text className="text-2xl font-semibold">
              {t(workerType.toLowerCase() as LowerWorkerType)}
            </Text>
            <Spacer />
            <Avatar>
              <AvatarFallback>
                <Icon
                  iconName="UserRoundIcon"
                  className="text-primary-on-fill"
                />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            <Text className="text-xl">
              {search.addedPersonFamilyName} {search.addedPersonGivenName}
            </Text>
          </HStack>
          <Spacer />
          <HStack className="items-center">
            <Icon iconName="SquareCheckBig" text={t('completedBy')} />
            <Spacer />
            <Avatar className="grid place-items-center size-8">
              <AvatarImage src={session?.person.avatarUrl} />
              <AvatarFallback>
                <Icon
                  iconName="UserRoundIcon"
                  className="text-primary-on-fill"
                />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            {session?.person?.name}
          </HStack>
        </VStack>
      </AttributeCard>
      <Spacer variant="xl" />
      <ActionButton
        variant="ghost"
        onClick={() => {
          navigate({
            to: '/person/add',
          });
        }}
        icon="PlusIcon"
        iconColor="active"
      >
        {t('continueAdding')}
      </ActionButton>
    </VStack>
  );
}
